import { memo, useCallback, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { MessageSquare, Network, Terminal, Eye } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { componentVisibilityState } from '@/state/layout';

type PanelKey = 'chat' | 'graph' | 'terminal';

const PANELS: Array<{
  key: PanelKey;
  label: string;
  icon: typeof MessageSquare;
  shortcut: string;
  shortcutKey: string;
}> = [
  { key: 'chat', label: 'Chat', icon: MessageSquare, shortcut: 'Alt+C', shortcutKey: 'c' },
  { key: 'graph', label: 'Graph', icon: Network, shortcut: 'Alt+G', shortcutKey: 'g' },
  { key: 'terminal', label: 'Terminal', icon: Terminal, shortcut: 'Alt+T', shortcutKey: 't' }
];

/**
 * LayoutControls component
 *
 * Segmented toggle group for Chat / Graph / Terminal panel visibility.
 * - Click a pill to show/hide that panel.
 * - "Show all" button on the right resets every panel to visible.
 * - Alt+C / Alt+G / Alt+T are global keyboard shortcuts.
 * - A small dot on each pill indicates the current state (filled = visible).
 * - At least one panel is always visible; the last-active one cannot be hidden.
 */
const LayoutControls = memo(() => {
  const [visibility, setVisibility] = useRecoilState(componentVisibilityState);

  const toggle = useCallback(
    (component: PanelKey) => {
      setVisibility((prev) => {
        const next = { ...prev, [component]: !prev[component] };
        if (!next.chat && !next.graph && !next.terminal) {
          next[component] = true;
        }
        return next;
      });
    },
    [setVisibility]
  );

  const showAll = useCallback(() => {
    setVisibility({ chat: true, graph: true, terminal: true });
  }, [setVisibility]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const key = event.key.toLowerCase();
      const match = PANELS.find((p) => p.shortcutKey === key);
      if (!match) return;
      event.preventDefault();
      toggle(match.key);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggle]);

  const allVisible = visibility.chat && visibility.graph && visibility.terminal;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-center gap-2">
        <div
          role="group"
          aria-label="Panel visibility"
          className="inline-flex items-center gap-0.5 rounded-full border border-border bg-muted/40 p-0.5 shadow-sm"
        >
          {PANELS.map(({ key, label, icon: Icon, shortcut }) => {
            const isActive = visibility[key];
            return (
              <Tooltip key={key}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isActive}
                    aria-label={`${isActive ? 'Hide' : 'Show'} ${label}`}
                    onClick={() => toggle(key)}
                    className={cn(
                      'relative inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
                      'transition-colors focus:outline-none focus:ring-2 focus:ring-ring/60',
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                    <span className="hidden md:inline">{label}</span>
                    <span
                      aria-hidden="true"
                      className={cn(
                        'ml-0.5 h-1.5 w-1.5 rounded-full transition-colors',
                        isActive ? 'bg-primary-foreground' : 'bg-muted-foreground/50'
                      )}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="flex flex-col items-center gap-0.5">
                  <span>{isActive ? `Hide ${label}` : `Show ${label}`}</span>
                  <kbd className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
                    {shortcut}
                  </kbd>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={showAll}
              disabled={allVisible}
              className={cn(
                'h-8 w-8 rounded-full text-muted-foreground hover:text-foreground',
                allVisible && 'opacity-50'
              )}
              aria-label="Show all panels"
            >
              <Eye className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {allVisible ? 'All panels visible' : 'Show all panels'}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
});

LayoutControls.displayName = 'LayoutControls';

export default LayoutControls;
